import { useState, useEffect, useCallback } from 'react';
import { useCollections } from './useCollections';
import { PendingInvitation } from '../types/collection';

export const useInvitations = () => {
  const { fetchInvitations, acceptInvitation, declineInvitation } = useCollections();
  const [invitations, setInvitations] = useState<PendingInvitation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchInvitations();
      setInvitations(data);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      setError(error.response?.data?.message || error.message || 'Failed to fetch invitations');
    } finally {
      setLoading(false);
    }
  }, [fetchInvitations]);

  const handleAccept = async (collectionId: string) => {
    try {
      await acceptInvitation(collectionId);
      setInvitations(prev => prev.filter(inv => inv.collectionId !== collectionId));
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      throw new Error(error.response?.data?.message || error.message || 'Failed to accept invitation');
    }
  };

  const handleDecline = async (collectionId: string) => {
    try {
      await declineInvitation(collectionId);
      setInvitations(prev => prev.filter(inv => inv.collectionId !== collectionId));
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      throw new Error(error.response?.data?.message || error.message || 'Failed to decline invitation');
    }
  };

  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    invitations,
    loading,
    error,
    refresh,
    acceptInvitation: handleAccept,
    declineInvitation: handleDecline
  };
};
